/* eslint-disable prefer-destructuring */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { debounce } from '@mui/material/utils';
import { GridFilterItem } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { MutableRefObject, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { usePage } from '../../../contexts/usePageContext';
import { setConfigStorageItem } from '../../../hooks/useConfigStorage';
import { useCurrentStructure } from '../../../hooks/useCurrentStructure';
import { changeCurrentPreset } from '../../../redux/clientConfiguration/clientConfigurationSlice';
import { RootState } from '../../../redux/store';
import { FormValues } from '../../../types/types';
import { findStructure, getTranslation } from '../../../utils/dataTypes';
import { CustomGridApi } from '../XTable/types';
import { DateRangeColumn } from './types';

const useFilterSidebar = ({
 apiRef,
}: {
 apiRef: MutableRefObject<CustomGridApi>;
}) => {
 const { pageName } = usePage();
 const dispatch = useDispatch();
 //  const allColumns = apiRef?.current?.getAllColumns();

 const currentColumnsPreset = useSelector(
  (state: RootState) =>
   state.clientConfiguration.currentColumnsPreset?.[pageName] || null,
 );
 const methods = useForm<Record<string, any>>();

 const structure = useCurrentStructure();

 const columns = apiRef?.current?.getAllColumns(); // useMemo(() => apiRef?.current?.getAllColumns(), [apiRef.current]);

 const [isFormChanged, setIsFormChanged] = useState(false);

 const dateRangeColumns = useMemo(
  () =>
   Object.keys(currentColumnsPreset?.filters?.fields || {})
    .map((key) => ({
     Name: key,
     Structure: findStructure(structure, key),
     Translation: getTranslation(structure, key),
    }))
    .filter((item) => item.Structure?.Type === 'datetime') as DateRangeColumn[],
  [currentColumnsPreset?.filters?.fields, structure],
 );

 const getValuesFromStorage = () => {
  // if (!Object.keys(currentColumnsPreset || {}).length || !structure)
  //  return false;

  const fieldsWithoutDates = Object.keys(
   currentColumnsPreset?.filters?.fields || {},
  )
   .filter((key) => findStructure(structure, key)?.Type !== 'datetime')
   .reduce(
    (acc, key) => ({
     ...acc,
     [key]:
      currentColumnsPreset?.filters?.fields[key] ||
      (findStructure(structure, key)?.Type === 'enum' ? [] : ''),
    }),
    {},
   ); // to replace null values with ''

  const dateFields = Object.keys(
   currentColumnsPreset?.filters?.fields || {},
  ).filter((key) => findStructure(structure, key)?.Type === 'datetime');

  // rewrite this mess
  let dateFieldsOutput = {};
  if (
   dateFields[0] &&
   typeof currentColumnsPreset?.filters?.fields[dateFields[0]] === 'string'
  ) {
   const dateString = currentColumnsPreset?.filters?.fields[dateFields[0]];
   if (typeof dateString === 'string' && dateString !== '') {
    // console.log('dateString', dateString);
    const dateArray = dateString.split('-');
    // console.log('dateArray', dateArray);
    if (dayjs(dateArray[0]).isValid()) {
     dateFieldsOutput = {
      ...dateFieldsOutput,
      DateFrom: dayjs(dateArray[0]),
     };
    } else if (dateArray[0] === 'x') {
     dateFieldsOutput = {
      ...dateFieldsOutput,
      DateFrom: '',
     };
    }
    if (dayjs(dateArray[1]).isValid()) {
     dateFieldsOutput = {
      ...dateFieldsOutput,
      DateTo: dayjs(dateArray[1]),
     };
    } else if (dateArray[1] === 'x') {
     dateFieldsOutput = {
      ...dateFieldsOutput,
      DateTo: '',
     };
    }
   }
  }

  const output = {
   ...fieldsWithoutDates,
   ...dateFieldsOutput,
   operators: currentColumnsPreset?.filters?.operators,
  };
  return output;
 };
 function fieldsWithDots(obj: Record<string, any>): Record<string, any> {
  // https://github.com/react-hook-form/react-hook-form/issues/3351
  const out: { [key: string]: any } = {};

  function walk(x: Record<string, any>, prefix: string[] = []): void {
   if (x instanceof dayjs) {
    out[[...prefix].join('.')] = x;
   } else {
    Object.entries(x).forEach(([key, val]) => {
     if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
      walk(val, [...prefix, key]);
     } else {
      out[[...prefix, key].join('.')] = val;
     }
    });
   }
  }

  walk(obj);
  return out;
 }
 const applyFilters = (data: FormValues) => {
  const { operators, ...fields } = data;
  const fixedOperators = fieldsWithDots(operators);
  const fixedFields = fieldsWithDots(fields);

  const allFilters = Object.entries(fixedFields).map(([key, value]) => {
   //  const column = allColumns.find((c) => c.field === key);
   if (
    // !column || // this was removed to allow filters like Bruto.Weight
    typeof value === 'undefined' ||
    ['period', 'DateField', 'DateFrom', 'DateTo'].includes(key)
   )
    return null;

   return {
    field: key,
    operator: fixedOperators[key],
    value,
   };
  }) as GridFilterItem[];
  console.log('allFilters', fields, allFilters);

  const activeFilters = allFilters.filter(
   (f) => f !== null && f.value && f.operator !== '', // and value?
  );

  // console.log(
  //  'fields',
  //  fields,
  //  fixedFields,
  //  fixedFields.DateField && fixedFields.DateFrom,
  //  fixedFields.DateField && fixedFields.DateTo,
  // );
  if (fixedFields.DateField && fixedFields.DateField !== '') {
   if (fixedFields.DateFrom && dayjs(fixedFields.DateFrom).isValid()) {
    activeFilters.push({
     field: fixedFields.DateField,
     operator: 'onOrAfter',
     value: dayjs(fixedFields.DateFrom).toDate(),
    });
   }
   if (fixedFields.DateTo && dayjs(fixedFields.DateTo).isValid()) {
    activeFilters.push({
     field: fixedFields.DateField,
     operator: 'onOrBefore',
     value: dayjs(fixedFields.DateTo).toDate(),
    });
   }
  }
  console.log('activeFilters', activeFilters);
  // send to datagrid
  apiRef.current.setFilterModel(
   {
    items: activeFilters.map((item, index) => ({
     ...item,
     id: `${item.field}-${JSON.stringify(item.value).replace(/[^a-zA-Z0-9]/g, '')}`,
    })),
   },
   'upsertFilterItems',
  );

  const newPresetFields = Object.keys(fixedFields)
   .filter(
    (field) =>
     ![
      'DateField',
      'DateFrom',
      'DateTo',
      'period',
      'year',
      'month',
      'week',
     ].includes(field),
   )
   .map((field) => ({
    [field]: fixedFields[field],
   }));

  // console.log('fixedFields', fixedFields);
  if (fixedFields.DateField) {
   let dateString = '';
   if (!!fixedFields.DateFrom && dayjs(fixedFields.DateFrom).isValid()) {
    console.log('1');
    dateString = dayjs(fixedFields.DateFrom).format('YYYY.MM.DD HH:mm:ss');
   } else {
    console.log('2');
    dateString = 'x';
   }
   if (!!fixedFields.DateTo && dayjs(fixedFields.DateTo).isValid()) {
    console.log('3');
    dateString = `${dateString}-${dayjs(fixedFields.DateTo).format('YYYY.MM.DD HH:mm:ss')}`;
   } else {
    console.log('4');
    dateString = `${dateString}-x`;
   }
   console.log('if (fields.DateField)', dateString);
   newPresetFields.push({
    [fixedFields.DateField]: dateString,
   });
  }

  const newPresetFieldsObj = newPresetFields.reduce(
   (acc, curr) => ({ ...acc, ...curr }),
   {},
  );

  console.log(newPresetFieldsObj);

  const preset = {
   ...currentColumnsPreset,
   name: currentColumnsPreset?.name || '',
   columns: currentColumnsPreset?.columns || [],
   filters: { fields: newPresetFieldsObj, operators: fixedOperators },
  };

  console.log('applyFilters', preset);

  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    preset,
    temp: true,
   }),
  );
  dispatch(
   changeCurrentPreset({
    page: pageName,
    type: 'columns',
    preset,
    temp: false,
   }),
  );
  // dispatch(getCurrentPresetStart({ table: pageName, type: 'columns' }));

  setConfigStorageItem({
   page: pageName,
   key: 'columns',
   value: preset,
  });

  //   handleClose(false);
 };

 const resetFilters = () => {
  // apiRef.current.setFilterModel({ items: [] });
  const allFieldKeys = Object.keys(methods.getValues());
  const resetValues = allFieldKeys.reduce<Record<string, string>>(
   (acc, key) => {
    acc[key] = key === 'DateField' ? methods.getValues().DateField : '';
    return acc;
   },
   {},
  );
  const { DateField, ...rest } = resetValues;
  console.log('RESET FILTERS FUNC', resetValues);
  methods.reset(
   rest,
   //   {
   //  keepDirty: true,
   //  keepTouched: true,
   // }
  );
  applyFilters(resetValues);
  // methods.resetField('Carrier', { keepDirty: true });
 };

 function getValue<T extends Record<string, any>>(obj: T, path: string): any {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj as any);
 }

 useEffect(() => {
  // activate operator if value is not empty and remove if
  const debouncedHandler = debounce((value, { name, type }) => {
   if (name && !name.includes('operators')) {
    const currentValue = name.includes('.')
     ? getValue(value, name)
     : value?.[name];
    console.log(currentValue, name);
    // is empty string, or empty object, or empty array
    const isEmpty =
     currentValue === undefined ||
     currentValue === '' ||
     (!!currentValue &&
      !Array.isArray(currentValue) &&
      typeof currentValue === 'object' &&
      Object.keys(currentValue).length === 0) ||
     (Array.isArray(currentValue) && currentValue?.length === 0);

    // console.log(
    //  currentValue === undefined,
    //  currentValue === '',
    //  !!currentValue &&
    //   !Array.isArray(currentValue) &&
    //   typeof currentValue === 'object' &&
    //   Object.keys(currentValue).length === 0,
    //  Array.isArray(currentValue) && currentValue?.length === 0,
    // );

    // #########################################################
    // REWRITE THIS
    // #########################################################

    // console.log(typeof currentValues, currentValues, value);

    // console.log(
    //  name,
    //  currentValue,
    //  isEmpty,
    //  currentValues?.[name],
    //  value?.[name],
    // );

    if (
     !isEmpty &&
     (value?.operators?.[name] === '' || value?.operators?.[name] === undefined)
    ) {
     console.log('empty operator');
     methods.setValue(`operators[${name}]`, 'equals'); // warning
    } else if (isEmpty) {
     console.log('empty value', `operators[${name}]`);
     methods.setValue(`operators[${name}]`, '');
    } else {
     console.log(
      'not empty',
      `operators[${name}]`,
      value?.operators?.[name],
      value?.operators?.[name],
     );
     methods.setValue(
      `operators[${name}]`,
      value?.operators?.[name] || 'equals',
     );
    }
    setIsFormChanged(true);
   }
  }, 300);

  const subscription = methods.watch(debouncedHandler);

  return () => {
   subscription.unsubscribe();
  };
 }, [methods.watch]);

 useEffect(() => {
  const vals = getValuesFromStorage();

  if (vals) {
   const output = {
    ...vals,
   };

   methods.reset(output);
  }
 }, [currentColumnsPreset, structure]);

 //  useEffect(() => {
 //   // how to prevent this from running on change from sidebar
 //   const unsubscribe = apiRef.current.subscribeEvent(
 //    'filterModelChange',
 //    (model) => {},
 //   );

 //   return () => {
 //    unsubscribe(); // Cleanup
 //   };
 //  }, [apiRef]);

 return {
  applyFilters,
  resetFilters,
  methods,
  columns,
  dateRangeColumns,
  currentColumnsPreset,
  structure,
  isFormChanged,
 };
};

export default useFilterSidebar;
